import {
  Bold01,
  Button,
  Check,
  CheckDone01,
  CodeSquare01,
  Heading01,
  Italic01,
  List,
  Separator,
  TagBlock,
  XClose,
} from "@dust-tt/sparkle";
import type { Editor } from "@tiptap/react";
import type { ReactNode } from "react";
// biome-ignore lint/correctness/noUnusedImports: ignored using `--suppress`
import React from "react";
import { useTranslation } from "react-i18next";

interface InstructionsMenuBarProps {
  editor: Editor | null;
  onAcceptAll: () => void;
  onRejectAll: () => void;
  showSuggestionActions?: boolean;
  toolbarExtra?: ReactNode;
}

export function InstructionsMenuBar({
  editor,
  onAcceptAll,
  onRejectAll,
  showSuggestionActions = false,
  toolbarExtra,
}: InstructionsMenuBarProps) {
  const { t } = useTranslation();

  if (!editor) {
    return null;
  }

  return (
    <div className="flex flex-1 flex-wrap items-center gap-2 px-3 py-2">
      <Button
        icon={Heading01}
        size="icon"
        variant="ghost-secondary"
        tooltip={t("builder.instructionsHeadingTooltip", "Heading")}
        onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
      />
      <Button
        icon={Bold01}
        size="icon"
        variant="ghost-secondary"
        tooltip={t("builder.instructionsBoldTooltip", "Bold")}
        tooltipShortcut="Cmd+B"
        onClick={() => editor.chain().focus().toggleBold().run()}
      />
      <Button
        icon={Italic01}
        size="icon"
        variant="ghost-secondary"
        tooltip={t("builder.instructionsItalicTooltip", "Italic")}
        tooltipShortcut="Cmd+I"
        onClick={() => editor.chain().focus().toggleItalic().run()}
      />
      <Separator orientation="vertical" />
      <Button
        icon={CheckDone01}
        size="icon"
        variant="ghost-secondary"
        tooltip={t("builder.instructionsBulletedListTooltip", "Bulleted list")}
        onClick={() => editor.chain().focus().toggleBulletList().run()}
      />
      <Button
        icon={List}
        size="icon"
        variant="ghost-secondary"
        tooltip={t("builder.instructionsOrderedListTooltip", "Ordered list")}
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
      />
      <Separator orientation="vertical" />
      <Button
        icon={CodeSquare01}
        size="icon"
        variant="ghost-secondary"
        tooltip={t("builder.instructionsCodeBlockTooltip", "Code Block")}
        onClick={() => editor.chain().focus().toggleCodeBlock().run()}
      />
      <Separator orientation="vertical" />
      <Button
        icon={TagBlock}
        size="icon"
        variant="ghost-secondary"
        tooltip={t("builder.instructionsXmlTagTooltip", "XML tag")}
        onClick={() => editor.chain().focus().insertInstructionBlock().run()}
      />
      <Separator orientation="vertical" />
      {toolbarExtra}
      <div className="flex-1" />
      {showSuggestionActions && (
        <div className="ml-auto flex gap-2">
          <Button
            size="xs"
            variant="outline"
            icon={XClose}
            label={t("builder.instructionsRejectAllLabel", "Reject all")}
            tooltip={t(
              "builder.instructionsRejectAllTooltip",
              "Reject all suggestions"
            )}
            onClick={onRejectAll}
          />
          <Button
            size="xs"
            icon={Check}
            variant="highlight-secondary"
            label={t("builder.instructionsAcceptAllLabel", "Accept all")}
            tooltip={t(
              "builder.instructionsAcceptAllTooltip",
              "Accept all suggestions"
            )}
            onClick={onAcceptAll}
          />
        </div>
      )}
    </div>
  );
}
